import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { SchedaIntervento } from '@/core/models/models/pages/scheda-intervento';
import { SchedaInterventoService } from '@/core/service/scheda-intervento.service';

type Mode = 'new' | 'edit' | 'view';

@Component({
    selector: 'app-scheda-interventi-form',
    standalone: true,
    providers: [MessageService],
    imports: [CommonModule, ReactiveFormsModule, ButtonModule, InputTextModule, CheckboxModule, TextareaModule, DatePickerModule, ToastModule],
    templateUrl: './scheda-interventi-form.component.html',
    styleUrl: './scheda-interventi-form.component.scss'
})
export class SchedaInterventiFormComponent implements OnInit {
    mode: Mode = 'new';
    saving = false;
    form!: FormGroup;

    constructor(
        private fb: FormBuilder,
        private service: SchedaInterventoService,
        private messageService: MessageService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.buildForm();

        const id = this.route.snapshot.paramMap.get('id');
        const qpMode = this.route.snapshot.queryParamMap.get('mode');

        if (!id) {
            this.mode = 'new';
        } else {
            this.mode = qpMode === 'view' ? 'view' : 'edit';
            this.loadItem(Number(id));
        }
    }

    get isNew(): boolean {
        return this.mode === 'new';
    }
    get isReadonly(): boolean {
        return this.mode === 'view';
    }
    get title(): string {
        return this.mode === 'new' ? 'Nuovo intervento' : this.mode === 'view' ? 'Dettaglio intervento' : 'Modifica intervento';
    }

    private buildForm(): void {
        this.form = this.fb.group({
            id: [null],
            dataInizioLavoro: [null, Validators.required],
            interventoGenericoFlg: [false],
            revisioneDeviatoioFlg: [false],
            sostituzioneDeviatoioFlg: [false],
            descrizioneIntervento: ['', Validators.required],
            dataVerifica: [null],
            flgEsitoPositivo: [false],
            flgEsitoNegativo: [false],
            flgEsitoPositivoConRiserva: [false],
            noteInterventoRilevatore: [''],
            noteInterventoAssegnatore: [''],
            anagDeviatoio: [null],
            tecnicoRilevatore: [null],
            rilScSempliceId: [null],
            rilScDoppiId: [null],
            rilScIntDoppiId: [null]
        });
    }

    private loadItem(id: number): void {
        this.service.get(id).subscribe({
            next: (row) => {
                this.form.reset({
                    ...row,
                    dataInizioLavoro: row.dataInizioLavoro ? new Date(row.dataInizioLavoro) : null,
                    dataVerifica: row.dataVerifica ? new Date(row.dataVerifica) : null
                });
                if (this.isReadonly) this.form.disable();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Caricamento intervento non riuscito' })
        });
    }

    salva(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.messageService.add({ severity: 'warn', summary: 'Dati mancanti', detail: 'Compila i campi obbligatori (Data inizio lavoro e Descrizione).' });
            return;
        }

        this.saving = true;
        const dto = this.sanitizeScheda(this.form.getRawValue());
        const isNew = !dto.id;

        if (isNew) {
            const { id, ...body } = dto;
            this.service.create(body).subscribe({
                next: (created) => this.onSaved('Intervento creato', created),
                error: (err) => this.onSaveError(err, 'Creazione non riuscita')
            });
        } else {
            this.service.update(dto).subscribe({
                next: () => this.onSaved('Intervento aggiornato'),
                error: (err) => this.onSaveError(err, 'Aggiornamento non riuscito')
            });
        }
    }

    private onSaved(detail: string, created?: SchedaIntervento): void {
        this.saving = false;
        this.messageService.add({ severity: 'success', summary: 'Salvato', detail });
        // Resta sulla pagina; se era nuovo passa alla modifica del record creato.
        if (this.mode === 'new' && created?.id != null) {
            this.router.navigate(['/scheda-interventi', created.id], { replaceUrl: true });
        }
    }

    private onSaveError(err: any, fallback: string): void {
        this.saving = false;
        console.error(err);
        this.messageService.add({ severity: 'error', summary: 'Errore', detail: err?.error?.message || fallback });
    }

    annulla(): void {
        this.router.navigate(['/scheda-interventi']);
    }

    // ===== helpers (allineati al comportamento originale) =====
    private toNumberOrNull(v: any): number | null {
        if (v === '' || v === undefined || v === null) return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    }

    private toRelObject(idLike: any): { id: number } | null {
        const id = this.toNumberOrNull(idLike);
        return id != null ? { id } : null;
    }

    private sanitizeScheda(raw: any): any {
        const dto: any = { ...raw };

        // date → ISO string (com'era atteso prima dal backend)
        dto.dataInizioLavoro = raw.dataInizioLavoro instanceof Date ? raw.dataInizioLavoro.toISOString() : (raw.dataInizioLavoro ?? null);
        dto.dataVerifica = raw.dataVerifica instanceof Date ? raw.dataVerifica.toISOString() : (raw.dataVerifica ?? null);

        Object.keys(dto).forEach((k) => {
            if (dto[k] === '') dto[k] = null;
        });

        dto.rilScSemplice = this.toRelObject(raw.rilScSempliceId);
        dto.rilScDoppi = this.toRelObject(raw.rilScDoppiId);
        dto.rilScIntDoppi = this.toRelObject(raw.rilScIntDoppiId);

        delete dto.rilScSempliceId;
        delete dto.rilScDoppiId;
        delete dto.rilScIntDoppiId;

        return dto;
    }
}
